import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DataWithPagination } from "services/api";
import { getFleetVehicleContractSalesAPI } from "services/api/fleet/vehicles/contracts/api";
import { FleetVehicleContractSaleRow } from "services/api/fleet/vehicles/contracts/types";
import { initialDataWithPagination } from "services/api/consts";
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

import { FleetContractSaleDropdownProps } from "./types";
import { DropdownOption } from "../Dropdown/types";

import { DropdownAutocomplete } from "../DropdownAutocomplete";

const FleetContractSaleDropdown: React.FC<FleetContractSaleDropdownProps> = (props) => {
  const {
    name,
    vehicleId,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    onContractSelect,
    showLinkedToInvoice,
    showCancelled,
    autoSelectLast,
    onLoaded,
  } = props;

  const [searchValue, setSearchValue] = useState<string>("");
  const autoSelectedRef = useRef<boolean>(false);
  const pagination = initialDataWithPagination.pagination;

  const cacheKey = buildDropdownCacheKey(DropdownCacheKey.FleetContractSales, vehicleId, showLinkedToInvoice, showCancelled, searchValue);

  const { data, isLoading } = useDropdownCache<DataWithPagination<FleetVehicleContractSaleRow>>(
    cacheKey,
    () => getFleetVehicleContractSalesAPI(
      pagination,
      {
        vehicleId,
        showLinkedToInvoice,
        showCancelled,
        search: searchValue || undefined,
      },
    ),
    5 * 60 * 1000
  );

  const searchResults = data ?? initialDataWithPagination;

  useEffect(() => {
    autoSelectedRef.current = false;
  }, [vehicleId]);

  useEffect(() => {
    if (data && onLoaded) onLoaded(data.data.length);
  }, [data]);

  const handleSearchChange = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setSearchValue("");
  }, []);

  // Auto-select last contract or populate data for pre-set value
  useEffect(() => {
    if (autoSelectedRef.current || isLoading) return;
    if (searchResults.data.length === 0) return;

    if (value) {
      const preSelected = searchResults.data.find((c) => c.id === value);
      if (preSelected && onContractSelect) {
        autoSelectedRef.current = true;
        onContractSelect(preSelected);
      }
      return;
    }

    if (!autoSelectLast) return;

    const lastContract = searchResults.data[0];
    autoSelectedRef.current = true;

    const syntheticItem: DropdownOption = {
      value: lastContract.id,
      text: lastContract.contractNumber || "",
    };
    const syntheticEvent = { target: { dataset: {} } };
    onChange(syntheticEvent, name, syntheticItem);
    if (onContractSelect) onContractSelect(lastContract);
  }, [searchResults.data, isLoading, autoSelectLast, value]);

  const contractItems: DropdownOption[] = useMemo(() => searchResults.data.map((c) => ({
    value: c.id,
    text: (
      <div className="flex items-center justify-between w-full">
        <span>{c.contractNumber}</span>
        <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">
          {c.buyerName}
        </span>
      </div>
    ),
  })), [searchResults.data]);

  const handleChange = (e: any, fieldName: string, item: DropdownOption) => {
    onChange(e, fieldName, item);

    if (onContractSelect) {
      const selectedContract = searchResults.data.find((c) => c.id === item.value) || null;
      onContractSelect(selectedContract);
    }
  };

  return (
    <DropdownAutocomplete
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      value={value}
      direction={direction}
      width={width}
      height={height}
      options={contractItems}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      error={error}
      isLoading={isLoading}
    />
  );
};

export default React.memo(FleetContractSaleDropdown);
